// Advanced Search Bar - Main search interface with basic and advanced modes
// Replaces the old SearchBar.js with structure matching capabilities

import { useState, useEffect, useCallback } from 'react';
import { Input, Button, Icon, Dropdown, Label } from 'semantic-ui-react';
import useDebounce from '../hooks/useDebounce';
import AdvancedSearchModal from './AdvancedSearchModal';
import axios from 'axios';
import { parseMultiModuleQuery, isMultiModuleQuery, getModuleColor, getModuleIcon } from '../../utils/queryTokenDetector';
import { detectSearchScopeFromQuery } from '../../utils/searchScopeDetector';
import '../css/advanced-search.css';
import '../../pages/css/databasehome.css'; // For bubble styles

export default function AdvancedSearchBar({
  origData,
  onFilterData,
  setFilterResponse,
  setSearch,
  search,
  setOpenFilter,
  setHelpClicked,
  setClearFlag,
  helpClicked
}) {
  const [localSearch, setLocalSearch] = useState(search);
  const [modalOpen, setModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchType, setSearchType] = useState('all'); // 'all' | 'pattern' | 'serotype' | 'nmr' | 'species'
  const [fuzzyMatches, setFuzzyMatches] = useState([]); // Store fuzzy match suggestions
  const debouncedSearch = useDebounce(localSearch, 300);

  const hostingServer =
    process.env.NODE_ENV === 'development'
      ? 'http://localhost:8888/'
      : 'https://sugardb.cs.uct.ac.za/';

  // Build config based on search type
  const getSearchConfig = useCallback((type, query = '') => {
    const baseConfig = {
      modules: {
        carbuilder: { enabled: type === 'all' || type === 'pattern' },
        serotype: { enabled: type === 'all' || type === 'serotype' },
        nmr: { enabled: type === 'all' || type === 'nmr' },
        species: { enabled: type === 'all' || type === 'species' }
      }
    };

    // Add module-specific configs
    if (baseConfig.modules.carbuilder.enabled) {
      // Auto-detect search scope from query syntax
      const detectedScope = detectSearchScopeFromQuery(query);

      baseConfig.carbuilder = {
        searchScope: detectedScope, // Auto-detected from query
        stereochemistryMode: 'agnostic',
        enableWildcards: true,
        enabledMatchers: {
          mainChain: true,
          exactBranch: true,
          anywhere: true,
          wildcard: true
        }
      };
    }

    return baseConfig;
  }, []);

  // Simple text search using backend API
  // FIX: Use useRef to avoid recreating function and causing infinite loop
  const performSearch = useCallback(async (query, type = searchType) => {
    if (!query || query.trim() === '') {
      setFilterResponse('');
      onFilterData(origData);
      setSearch(''); // Sync parent state when clearing
      setIsLoading(false);
      setFuzzyMatches([]); // Clear fuzzy hints on empty query
      return;
    }

    setIsLoading(true);
    try {
      const config = getSearchConfig(type, query); // Pass query for auto-detection

      // PREPROCESSING: If scope is 'branches' and query is wrapped in brackets,
      // strip the brackets before sending to backend
      // This allows CLI to use [GlcA] for clarity while backend receives GlcA
      let processedQuery = query.trim();
      if (config.carbuilder && config.carbuilder.searchScope === 'branches') {
        const bracketMatch = processedQuery.match(/^\[(.+)\]$/);
        if (bracketMatch) {
          processedQuery = bracketMatch[1]; // Extract content inside brackets
        }
      }

      const response = await axios.post(`${hostingServer}api/search`, {
        query: processedQuery,
        config: config
      });

      //MK AI fix below
     /*
     const response = await axios.get(`${base}api/search`, {
  params: { query: processedQuery, config: JSON.stringify(config) }
});*/


      if (response.data.success) {
        const allMatches = response.data.results
          .filter(r => r.matched)
          .map(r => r.entry);

        // Extract fuzzy match information from matchDetails -> result.details
        const fuzzyResults = response.data.results
          .filter(r => r.matched && Array.isArray(r.matchDetails))
          .flatMap(r => r.matchDetails
            .map(d => d?.result?.details)
            .filter(d => d && d.fuzzyMatch));

        // Get unique fuzzy matches with the matched text and original query
        const uniqueFuzzyMatches = [];
        const seen = new Set();

        fuzzyResults.forEach(detail => {
          // Handle Species module format (single match with matchedText)
          if (detail.matchedText) {
            const key = `${detail.query || detail.searchQuery}-${detail.matchedText}`;
            if (!seen.has(key)) {
              seen.add(key);
              uniqueFuzzyMatches.push({
                original: detail.query || detail.searchQuery || query.trim(),
                suggestion: detail.matchedText,
                confidence: detail.confidence || 0
              });
            }
          }

          // Handle Carbuilder module format (array of fuzzy matches)
          if (Array.isArray(detail.fuzzyMatches)) {
            detail.fuzzyMatches.forEach(fm => {
              const key = `${fm.original}-${fm.suggestion}`;
              if (!seen.has(key)) {
                seen.add(key);
                uniqueFuzzyMatches.push({
                  original: fm.original,
                  suggestion: fm.suggestion,
                  confidence: fm.confidence || 0
                });
              }
            });
          }
        });

        setFuzzyMatches(uniqueFuzzyMatches);

        // Filter matches to only include entries from origData (species-filtered)
        // This ensures search respects the species toggle
        const origDataIds = new Set(origData.map(entry => entry._id));
        const matchedEntries = allMatches.filter(entry => origDataIds.has(entry._id));

        // FIX: Update all states together to prevent flickering
        onFilterData(matchedEntries);
        setSearch(query); // Sync parent state AFTER search completes

        if (matchedEntries.length === 0) {
          setFilterResponse(`No matches found for structure "${query}"${allMatches.length > 0 ? ` (${allMatches.length} matches in other species)` : ''}`);
        } else {
          setFilterResponse(
            `Found ${matchedEntries.length} match${matchedEntries.length !== 1 ? 'es' : ''} for structure "${query}"`
          );
        }
      }
    } catch (error) {
      /*
      console.error('Search error:', error);
      setFilterResponse('Search failed. Please try again.');
      setFuzzyMatches([]); // Do not keep stale suggestions on error
      */
// Log the useful parts:
  console.error('Search error message:', error?.message);
  console.error('Search error code:', error?.code);
  console.error('Search error response status:', error?.response?.status);
  console.error('Search error response data:', error?.response?.data);
  console.error('Search error request:', error?.request);

  const status = error?.response?.status;
  const serverMsg =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.response?.data?.details;

  setFilterResponse(
    status
      ? `Search failed (HTTP ${status})${serverMsg ? `: ${serverMsg}` : ''}`
      : `Search failed: ${error?.message || 'Network/CORS error'}`
  );
  setFuzzyMatches([]); // Do not keep stale suggestions on error


    } finally {
      setIsLoading(false);
    }
  }, [origData, onFilterData, setFilterResponse, setSearch, hostingServer, getSearchConfig, searchType]);

  // Execute search when debounced query changes
  // FIX: Remove performSearch from dependencies to prevent infinite loop
  useEffect(() => {
    // Always call performSearch, even with empty string, to ensure proper reset
    if (debouncedSearch === '' || debouncedSearch === null || debouncedSearch === undefined) {
      // Explicitly reset when empty
      setFilterResponse('');
      onFilterData(origData);
      setSearch('');
      setIsLoading(false);
    } else {
      performSearch(debouncedSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // Reactive search: Re-run search when origData changes (species toggle)
  // Only re-run if there's an active search query
  useEffect(() => {
    if (search && search.trim() !== '') {
      // Use the parent search state (not localSearch) to avoid debounce
      performSearch(search);
    } else {
      // No active search, just update to show all origData
      onFilterData(origData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origData]);

  // FIX: Removed immediate parent sync - now only syncs after search completes (inside performSearch)

  // Handle input change with special character normalization
  const handleChange = (e) => {
    setOpenFilter(false);
    const normalized = e.target.value
      .replace(/->/g, '→')
      .replace(/\\a/g, 'α')
      .replace(/\\b/g, 'β');
    setLocalSearch(normalized);
  };

  // Clear search and reset to original data
  // FIX: Prevent double update by resetting states in one batch
  const clearSearch = () => {
    setIsLoading(false); // Ensure loading spinner stops
    setLocalSearch(''); // This will trigger performSearch('') after debounce
    setSearch(''); // Clear parent immediately
    setFilterResponse('');
    onFilterData(origData); // Reset to original data immediately
    setClearFlag(true); // Signal FilterBar to reset
    setFuzzyMatches([]); // Clear fuzzy suggestions
  };

  // Open advanced search modal
  const openAdvancedSearch = () => {
    setModalOpen(true);
  };

  // Handle search from advanced modal
  const handleAdvancedSearch = async (pattern, config, modalSearchType = 'all') => {
    // UX FIX: Update UI immediately BEFORE closing modal for instant feedback
    setLocalSearch(pattern); // Show pattern in search bar immediately
    setSearch(pattern); // Update parent state
    setSearchType(modalSearchType); // Update search type from modal
    setIsLoading(true); // Show loading spinner immediately

    // Close modal after a brief delay to let user see the feedback
    setTimeout(() => setModalOpen(false), 100);

    try {
      // Merge modal config with search type config
      const typeConfig = getSearchConfig(modalSearchType);
      const mergedConfig = {
        ...typeConfig,
        ...config,
        modules: {
          ...typeConfig.modules,
          ...(config.modules || {})
        },
        // Deep merge carbuilder config to preserve bond settings
        carbuilder: {
          ...typeConfig.carbuilder,
          ...(config.carbuilder || {})
        }
      };

      // PREPROCESSING: If scope is 'branches' and query is wrapped in brackets,
      // strip the brackets before sending to backend
      let processedPattern = pattern;
      if (mergedConfig.carbuilder && mergedConfig.carbuilder.searchScope === 'branches') {
        const bracketMatch = pattern.match(/^\[(.+)\]$/);
        if (bracketMatch) {
          processedPattern = bracketMatch[1]; // Extract content inside brackets
        }
      }

      const response = await axios.post(`${hostingServer}api/search`, {
        query: processedPattern,
        config: mergedConfig
      });

      if (response.data.success) {
        const allMatches = response.data.results
          .filter(r => r.matched)
          .map(r => r.entry);

        // Extract fuzzy match information (same logic as quick search)
        const fuzzyResults = response.data.results
          .filter(r => r.matched && Array.isArray(r.matchDetails))
          .flatMap(r => r.matchDetails
            .map(d => d?.result?.details)
            .filter(d => d && d.fuzzyMatch));

        const uniqueFuzzyMatches = [];
        const seen = new Set();
        fuzzyResults.forEach(detail => {
          // Handle Species module format (single match with matchedText)
          if (detail.matchedText) {
            const key = `${detail.query || detail.searchQuery}-${detail.matchedText}`;
            if (!seen.has(key)) {
              seen.add(key);
              uniqueFuzzyMatches.push({
                original: detail.query || detail.searchQuery || pattern,
                suggestion: detail.matchedText,
                confidence: detail.confidence || 0
              });
            }
          }

          // Handle Carbuilder module format (array of fuzzy matches)
          if (Array.isArray(detail.fuzzyMatches)) {
            detail.fuzzyMatches.forEach(fm => {
              const key = `${fm.original}-${fm.suggestion}`;
              if (!seen.has(key)) {
                seen.add(key);
                uniqueFuzzyMatches.push({
                  original: fm.original,
                  suggestion: fm.suggestion,
                  confidence: fm.confidence || 0
                });
              }
            });
          }
        });
        setFuzzyMatches(uniqueFuzzyMatches);

        // Filter matches to only include entries from origData (species-filtered)
        const origDataIds = new Set(origData.map(entry => entry._id));
        const matchedEntries = allMatches.filter(entry => origDataIds.has(entry._id));

        // Update results
        onFilterData(matchedEntries);

        if (matchedEntries.length === 0) {
          setFilterResponse(`No matches found for structure "${pattern}"${allMatches.length > 0 ? ` (${allMatches.length} matches in other species)` : ''}`);
        } else {
          setFilterResponse(
            `Found ${matchedEntries.length} match${matchedEntries.length !== 1 ? 'es' : ''} for structure "${pattern}"`
          );
        }
      }
    } catch (error) {
      console.error('Advanced search error:', error);
      setFilterResponse('Search failed. Please try again.');
      setFuzzyMatches([]); // Clear fuzzy hints on error
    } finally {
      setIsLoading(false);
    }
  };

  // Search type options
  const searchTypeOptions = [
    { key: 'all', value: 'all', text: 'All Fields', icon: 'globe' },
    { key: 'pattern', value: 'pattern', text: 'Structure', icon: 'connectdevelop' },
    { key: 'serotype', value: 'serotype', text: 'Serotype', icon: 'tag' },
    { key: 'nmr', value: 'nmr', text: 'NMR', icon: 'chart line' },
    { key: 'species', value: 'species', text: 'Species', icon: 'dna' }
  ];

  // Get placeholder based on search type
  const getPlaceholder = () => {
    switch (searchType) {
      case 'pattern':
        return 'Search structures... e.g., Glc-Man-Gal or Glc-b4-Man-a3-Gal';
      case 'serotype':
        return 'Search serotype... e.g., K1, O157';
      case 'nmr':
        return 'Search NMR... e.g., 5.28 or 5.2-5.3';
      case 'species':
        return 'Search species... e.g., Klebsiella';
      case 'all':
      default:
        return 'Search all fields... e.g., O8Ec Man-Glc or "Escherichia coli" O157';
    }
  };

  // Parse tokens for preview (only if searchType is 'all')
  const tokenPreview = searchType === 'all' && localSearch && localSearch.trim()
    ? parseMultiModuleQuery(localSearch)
    : [];

  const showMultiModulePreview = searchType === 'all' && isMultiModuleQuery(localSearch);

  return (
    <>
      <div className="advanced-search-bar">
        <div className="search-type-selector">
          <Dropdown
            selection
            compact
            value={searchType}
            options={searchTypeOptions}
            onChange={(e, { value }) => {
              setSearchType(value);
              // Re-run search with new type if there's an active query
              if (localSearch && localSearch.trim()) {
                performSearch(localSearch, value);
              }
            }}
            className="search-type-dropdown"
          />
        </div>

        <div className="search-input-container">
          <Input
            fluid
            icon={isLoading ? 'spinner' : 'search'}
            loading={isLoading}
            type="text"
            placeholder={getPlaceholder()}
            value={localSearch}
            onClick={() => setHelpClicked(false)}
            onChange={handleChange}
            className="pattern-search-input"
          />

          {/* Fuzzy suggestion inline hint (ghost text style) */}
          {fuzzyMatches.length > 0 && (
            <div
              style={{
                marginTop: '6px',
                padding: '6px 10px',
                background: '#f5f5f5',
                borderRadius: '4px',
                color: '#666',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span style={{ color: '#999' }}>Did you mean:</span>
              {fuzzyMatches.slice(0, 3).map((m, idx) => (
                <button
                  key={`${m.suggestion}-${idx}`}
                  onClick={() => {
                    setLocalSearch(m.suggestion);
                    setSearch(m.suggestion);
                    performSearch(m.suggestion, searchType);
                  }}
                  style={{
                    background: '#e9ecef',
                    border: '1px solid #dfe3e6',
                    borderRadius: '12px',
                    padding: '4px 10px',
                    color: '#444',
                    cursor: 'pointer',
                    fontSize: '12px',
                    transition: 'all 0.15s ease'
                  }}
                >
                  {m.suggestion}
                  {m.confidence ? (
                    <span style={{ color: '#888', marginLeft: '6px', fontSize: '11px' }}>
                      ({Math.round(m.confidence)}%)
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
          )}

          {/* Multi-Module Token Preview */}
          {showMultiModulePreview && tokenPreview.length > 0 && (
            <div style={{
              marginTop: '8px',
              padding: '8px 12px',
              background: '#f8f9fa',
              borderRadius: '4px',
              fontSize: '12px',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px',
              alignItems: 'center'
            }}>
              <span style={{ color: '#666', fontWeight: '500' }}>
                Detected modules:
              </span>
              {tokenPreview.map((item, index) => (
                <Label
                  key={index}
                  size="small"
                  style={{
                    backgroundColor: getModuleColor(item.module),
                    color: item.module === 'species' ? '#333' : '#fff',
                    border: 'none',
                    fontWeight: '500'
                  }}
                >
                  <Icon name={getModuleIcon(item.module)} />
                  {item.token}
                  <Label.Detail>{item.module}</Label.Detail>
                </Label>
              ))}
              <span style={{ color: '#999', fontSize: '11px', marginLeft: '4px' }}>
                (AND logic)
              </span>
            </div>
          )}
        </div>

        <div className="search-actions">
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            {helpClicked && (
              <div className="bubble-box-under">
                <p>Configure matcher types, stereochemistry, and build visual structures</p>
              </div>
            )}
            <Button
              size="medium"
              onClick={openAdvancedSearch}
              className="advanced-search-button"
            >
              <Icon name="sliders horizontal" />
              Advanced Search
            </Button>
          </div>

          <button className="clear-search-button" onClick={clearSearch}>
            <Icon name="redo" />
            Clear
          </button>
        </div>
      </div>

      {modalOpen && (
        <AdvancedSearchModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSearch={handleAdvancedSearch}
          initialPattern={localSearch}
          initialSearchType={searchType}
        />
      )}
    </>
  );
}
